import {
    Component,
    Inject,
    OnInit,
    ViewChild,
    ElementRef,
} from '@angular/core';
import {
    MAT_DIALOG_DATA,
    MatDialogRef,
    MatDialog,
} from '@angular/material/dialog';
import { AuthService } from '@services/auth/auth.service';
import {
    ConsultasOrdenService,
    ValidarGeneracionRequestDto,
    GenerarOrdenRequestDto,
} from '@services/orden-consultas/signo-orden-consultas.service';

/** =====================
 *  Modelos internos
 *  ===================== */
interface UsuarioOrden {
    _id: string;
    nombre: string;
    apellido: string;
    identificacion: string;
    parentesco: string;
}

@Component({
    selector: 'app-orden-medica-modal',
    templateUrl: './orden-medica-modal.component.html',
    styleUrls: ['./orden-medica-modal.component.scss'],
})
export class OrdenMedicaModalComponent implements OnInit {
    constructor(
        @Inject(MAT_DIALOG_DATA)
        public data: {
            titular: any;
            grupoFamiliar: UsuarioOrden[];
        },
        private dialogRef: MatDialogRef<OrdenMedicaModalComponent>,
        private dialog: MatDialog,
        private consultasSrv: ConsultasOrdenService,
        private auth: AuthService,
    ) {}

    /** =====================
     *  View
     *  ===================== */
    @ViewChild('ordenHtmlRef') ordenHtmlRef!: ElementRef;

    /** =====================
     *  Estado UI
     *  ===================== */
    validando = true;
    puedeGenerar = false;
    generando = false;
    generada = false;
    mostrandoPreview = true;
    mensajeValidacion = '';

    /** =====================
     *  Beneficiarios
     *  ===================== */
    familiares: UsuarioOrden[] = [];
    usuarioSeleccionado!: UsuarioOrden;

    /** =====================
     *  Datos backend
     *  ===================== */
    private tipoOrdenId: string | null = null;
    fecha = new Date();

    /** =====================
     *  Modelo Orden
     *  ===================== */
    modeloOrden = {
        tipo: '—',
        sede: 'Sede Central',
        apellidoNombre: '',
        identificacion: '',
        nroOrden: '—',
        plan: '—',
        edad: '—',
    };

    /** =====================
     *  INIT
     *  ===================== */
    async ngOnInit(): Promise<void> {
        this.armarBeneficiarios();
        await this.obtenerTipoOrdenYValidar();
    }

    /** =====================
     *  Beneficiarios
     *  ===================== */
    private armarBeneficiarios(): void {
        console.log('data', this.data);
        const titular = this.data.titular;

        this.familiares = [
            {
                _id: titular._id,
                nombre: titular.nombrePersona,
                apellido: titular.apellidoPersona,
                identificacion: titular.identificacion,
                parentesco: 'TITULAR',
            },
            ...(this.data.grupoFamiliar || []),
        ];

        this.usuarioSeleccionado = this.familiares[0]; // TITULAR por defecto
    }

    /** =====================
     *  Tipo + Validación
     *  ===================== */
    private async obtenerTipoOrdenYValidar(): Promise<void> {
        this.validando = true;

        this.consultasSrv.getTiposOrden$().subscribe({
            next: (tipos) => {
                const tipo = tipos?.[0];
                if (!tipo?._id) {
                    this.mensajeValidacion =
                        'No hay tipos de orden disponibles.';
                    this.puedeGenerar = false;
                    this.validando = false;
                    return;
                }

                // ===== NUEVO: validación por sede =====
                const sedesTipo: string[] = tipo.sedes || [];
                const sedesTitular: string[] =
                    this.data?.titular?.signoSedes || [];

                const sedeHabilitada = this.tieneSedeHabilitada(
                    sedesTipo,
                    sedesTitular,
                );

                if (!sedeHabilitada) {
                    const nombreSede = this.obtenerNombreSedeFallback();

                    this.mensajeValidacion = `Para ${nombreSede} no están disponibles las órdenes de consulta por el momento.`;
                    this.puedeGenerar = false;
                    this.validando = false;
                    return; // ⛔ NO sigue
                }

                // ===== si pasa sede, seguimos normal =====
                this.tipoOrdenId = tipo._id;
                this.modeloOrden.tipo = tipo.nombre || this.modeloOrden.tipo;

                this.validar(); // ← tu validación existente
            },
            error: () => {
                this.mensajeValidacion = 'Error cargando tipos de orden.';
                this.puedeGenerar = false;
                this.validando = false;
            },
        });
    }

    // private async obtenerTipoOrdenYValidar(): Promise<void> {
    //     this.validando = true;

    //     this.consultasSrv.getTiposOrden$().subscribe({
    //         next: (tipos) => {
    //             const tipo = tipos?.[0];
    //             if (!tipo?._id) {
    //                 this.mensajeValidacion =
    //                     'No hay tipos de orden disponibles.';
    //                 this.validando = false;
    //                 return;
    //             }

    //             this.tipoOrdenId = tipo._id;
    //             this.modeloOrden.tipo = tipo.nombre || this.modeloOrden.tipo;

    //             this.validar();
    //         },
    //         error: () => {
    //             this.mensajeValidacion = 'Error cargando tipos de orden.';
    //             this.validando = false;
    //         },
    //     });
    // }

    private validar(): void {
        if (!this.tipoOrdenId) return;

        const dto: ValidarGeneracionRequestDto = {
            tipo: this.tipoOrdenId,
            usuario: this.usuarioSeleccionado._id,
            when: new Date().toISOString(),
            groupBy: 'estadoTipo',
        };

        this.consultasSrv.validarConMensaje(dto).subscribe({
            next: (r) => {
                this.puedeGenerar = r.ok;
                this.mensajeValidacion = r.message || '';
                this.validando = false;
            },
            error: () => {
                this.puedeGenerar = false;
                this.mensajeValidacion = 'No se pudo validar la orden.';
                this.validando = false;
            },
        });
    }

    /** =====================
     *  Acciones
     *  ===================== */
    cerrar(): void {
        this.dialogRef.close(false);
    }

    async confirmarYGenerar(): Promise<void> {
        if (!this.puedeGenerar || this.generando || !this.tipoOrdenId) return;

        const ok = await this.confirmar(
            '¿Generar Orden?',
            `La orden se emitirá para ${this.usuarioSeleccionado.apellido}, ${this.usuarioSeleccionado.nombre}.`,
        );
        if (!ok) return;

        this.generando = true;

        console.log('GenerarOrdenRequestDto', this.usuarioSeleccionado._id);
        const dto: GenerarOrdenRequestDto = {
            tipo: this.tipoOrdenId,
            usuario: this.usuarioSeleccionado._id,
        };

        this.consultasSrv.generarConMensaje(dto).subscribe({
            next: (r) => {
                this.generada = true;
                this.generando = false;

                this.fecha = new Date(r.data!.fecha);
                this.modeloOrden.nroOrden = r.data!.numeroOrden;
                this.modeloOrden.apellidoNombre = r.data!.nombreApellido;
                this.modeloOrden.identificacion = r.data!.numeroBeneficiario;
                this.modeloOrden.plan = r.data!.plan;
                this.modeloOrden.edad = String(r.data!.edad);
            },
            error: (e) => {
                this.generando = false;
                this.mensajeValidacion =
                    e.message || 'No se pudo generar la orden.';
            },
        });
    }

    /** =====================
     *  Confirm dialog
     *  ===================== */
    private confirmar(titulo: string, mensaje: string): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            const ref = this.dialog.open(SimpleConfirmDialog, {
                width: '380px',
                data: { titulo, mensaje },
            });
            ref.afterClosed().subscribe((r) => resolve(!!r));
        });
    }

    private tieneSedeHabilitada(
        sedesTipo: string[],
        sedesTitular: string[],
    ): boolean {
        if (!sedesTipo?.length || !sedesTitular?.length) return false;

        return sedesTitular.some((sedeId) => sedesTipo.includes(sedeId));
    }

    private obtenerNombreSedeFallback(): string {
        return 'su sede actual';
    }

    get titularItem(): UsuarioOrden | null {
        return this.familiares?.length ? this.familiares[0] : null;
    }

    get familiaresACargo(): UsuarioOrden[] {
        return this.familiares?.length > 1 ? this.familiares.slice(1) : [];
    }
}

/** =====================
 *  Dialog simple
 *  ===================== */
@Component({
    selector: 'simple-confirm-dialog',
    template: `
        <div class="confirm-dialog">
            <h3 class="title">{{ data.titulo }}</h3>

            <p class="message">{{ data.mensaje }}</p>

            <div class="actions">
                <button
                    type="button"
                    class="btn btn-cancel"
                    (click)="close(false)"
                >
                    Cancelar
                </button>

                <button
                    type="button"
                    class="btn btn-confirm"
                    (click)="close(true)"
                    cdkFocusInitial
                >
                    Generar
                </button>
            </div>
        </div>
    `,
    styles: [
        `
            .confirm-dialog {
                padding: 20px 16px 8px;
                text-align: center;
                font-family: inherit;
            }

            .title {
                margin: 0 0 10px;
                font-size: 20px;
                font-weight: 800;
                color: #580f2e; /* bordó */
            }

            .message {
                margin: 0 0 22px;
                font-size: 14px;
                color: #555;
                line-height: 1.4;
            }

            .actions {
                display: flex;
                justify-content: center;
                gap: 14px;
            }

            .btn {
                min-width: 130px;
                padding: 10px 18px;
                border-radius: 10px;
                font-weight: 700;
                font-size: 14px;
                cursor: pointer;
                border: 2px solid transparent;
                transition: all 0.2s ease;
            }

            /* ===== Cancelar (gris) ===== */
            .btn-cancel {
                background: #f2f2f2;
                color: #555;
                border-color: #d0d0d0;
            }

            .btn-cancel:hover {
                background: #e6e6e6;
                color: #333;
            }

            /* ===== Generar (bordó) ===== */
            .btn-confirm {
                background: #580f2e;
                color: #fff;
                border-color: #580f2e;
            }

            .btn-confirm:hover {
                background: #fff;
                color: #580f2e;
            }
        `,
    ],
})
export class SimpleConfirmDialog {
    constructor(
        private ref: MatDialogRef<SimpleConfirmDialog>,
        @Inject(MAT_DIALOG_DATA)
        public data: {
            titulo: string;
            mensaje: string;
        },
    ) {}

    close(v: boolean) {
        this.ref.close(v);
    }
}

// import {
//     Component,
//     Inject,
//     OnInit,
//     ViewChild,
//     ElementRef,
// } from '@angular/core';
// import {
//     MAT_DIALOG_DATA,
//     MatDialogRef,
//     MatDialog,
// } from '@angular/material/dialog';
// import { OrdenMedicaModalComponent as _Ignore } from './orden-medica-modal.component'; // evita import circular del mismo archivo si tu IDE lo hace
// import { AuthService } from '@services/auth/auth.service';
// import {
//     ConsultasOrdenService,
//     ValidarGeneracionRequestDto,
//     GenerarOrdenRequestDto,
// } from '@services/orden-consultas/signo-orden-consultas.service';

// type PosKey =
//     | 'nroOrden'
//     | 'fecha'
//     | 'benef'
//     | 'plan'
//     | 'apellido'
//     | 'edad'
//     | 'prestador'
//     | 'firmaBenef'
//     | 'fechaPrest'
//     | 'firmaPrest';

// @Component({
//     selector: 'app-orden-medica-modal',
//     templateUrl: './orden-medica-modal.component.html',
//     styleUrls: ['./orden-medica-modal.component.scss'],
// })
// export class OrdenMedicaModalComponent implements OnInit {
//     constructor(
//         @Inject(MAT_DIALOG_DATA) public data: { usuario: any },
//         private dialogRef: MatDialogRef<OrdenMedicaModalComponent>,
//         private dialog: MatDialog,
//         private consultasSrv: ConsultasOrdenService,
//         private auth: AuthService
//     ) {}

//     @ViewChild('ordenHtmlRef') ordenHtmlRef!: ElementRef;
//     @ViewChild('ordenPdfRef') ordenPdfRef!: ElementRef;

//     /** ===== Flags / estado de UI ===== */
//     validando = true;
//     puedeGenerar = false;
//     mensajeValidacion = '';
//     generando = false;

//     /** Hardcode temporal pedido por vos */
//     private tipoOrdenId: string | null = null;

//     private userId: string | null = null;

//     operador = 'Francisco Gielis';
//     generada = false;
//     mostrandoPreview = true;
//     fecha = new Date();

//     modeloOrden = {
//         tipo: '—',
//         sede: 'Sede Central',
//         apellidoNombre: 'Nombre Apellido',
//         identificacion: '00000000',
//         nroOrden: '—',
//         plan: 'Plan A',
//         edad: '—',
//         prestador: '-',
//     };

//     /** ===== PDF helpers y posicionamiento (tus mismos datos) ===== */
//     private readonly mm = (n: number) => n * 2.834645669;
//     private readonly POS = {
//         nroOrden: { x: 140, y: 10 },
//         fecha: { x: 140, y: 22 },
//         plan: { x: 152, y: 60 },
//         edad: { x: 152, y: 68 },
//         benef: { x: 48, y: 60 },
//         apellido: { x: 48, y: 68 },
//         prestador: { x: 75, y: 121 },
//         firmaBenef: { x: 40, y: 275 },
//         fechaPrest: { x: 100, y: 275 },
//         firmaPrest: { x: 165, y: 275 },
//     };
//     private readonly FONT = {
//         tiny: 8,
//         small: 9,
//         normal: 11,
//         big: 12,
//         title: 14,
//     };
//     private readonly DEBUG_GUIDES = false;
//     private readonly DELTA = {
//         nroOrden: { dx: 0, dy: 0 },
//         fecha: { dx: 0, dy: 0 },
//         benef: { dx: 0, dy: 0 },
//         plan: { dx: 0, dy: 0 },
//         apellido: { dx: 0, dy: 0 },
//         edad: { dx: 0, dy: 0 },
//         prestador: { dx: 0, dy: 0 },
//     };
//     private readonly SHOW_GRID = false;
//     private readonly GRID_STEP_MM = 5;
//     private readonly GRID_BOLD_EVERY = 10;
//     private readonly A4_W_PT = 595.28;
//     private readonly A4_H_PT = 841.89;

//     async ngOnInit(): Promise<void> {
//         // 1) Obtener userId
//         this.userId = await this.auth.loadIdUsuario();
//         if (!this.userId) {
//             this.puedeGenerar = false;
//             this.mensajeValidacion =
//                 'No pudimos identificar tu sesión. Ingresá nuevamente.';
//             this.validando = false;
//             return;
//         }

//         // 2) Traer tipo(s) de orden de la API (siempre retorna 1 registro)
//         this.validando = true;
//         this.puedeGenerar = false;

//         this.consultasSrv.getTiposOrden$().subscribe({
//             next: (tipos) => {
//                 const unico = tipos?.[0];
//                 if (!unico?._id) {
//                     this.puedeGenerar = false;
//                     this.mensajeValidacion =
//                         'No hay tipos de orden disponibles.';
//                     // this.cargando = false;
//                     return;
//                 }
//                 this.tipoOrdenId = unico._id;
//                 if (unico.nombre) this.modeloOrden.tipo = unico.nombre;
//                 this.runValidacion(); // ← valida después de obtener tipo
//             },
//             error: (e) => {
//                 //   this.cargando = false;
//                 this.puedeGenerar = false;
//                 this.mensajeValidacion =
//                     e?.message || 'No se pudieron cargar los tipos de orden.';
//             },
//         });
//     }

//     /** ====== Validación previa (habilita o no el botón) ====== */
//     private runValidacion(): void {
//         if (!this.tipoOrdenId) {
//             this.validando = false;
//             this.puedeGenerar = false;
//             this.mensajeValidacion = 'No se encontró el tipo de orden.';
//             return;
//         }

//         this.validando = true;
//         this.puedeGenerar = false;
//         this.mensajeValidacion = '';

//         const dto: ValidarGeneracionRequestDto = {
//             tipo: this.tipoOrdenId, // ← ahora dinámico
//             usuario: this.userId!,
//             when: new Date().toISOString(),
//             groupBy: 'estadoTipo',
//         };

//         this.consultasSrv.validarConMensaje(dto).subscribe({
//             next: (r) => {
//                 this.validando = false;
//                 this.puedeGenerar = r.ok;
//                 this.mensajeValidacion = r.message || '';
//             },
//             error: (e: Error) => {
//                 this.validando = false;
//                 this.puedeGenerar = false;
//                 this.mensajeValidacion =
//                     e.message || 'No se pudo validar la orden.';
//             },
//         });
//     }

//     cerrar(): void {
//         this.dialogRef.close(false);
//     }

//     /** Confirmación + generación real contra backend */
//     async confirmarYGenerar(): Promise<void> {
//         if (!this.puedeGenerar || this.generando) return;
//         if (!this.tipoOrdenId) {
//             this.mensajeValidacion = 'No se encontró el tipo de orden.';
//             return;
//         }

//         const ok = await this.pedirConfirmacion(
//             '¿Generar Orden?',
//             'Se registrará la orden de consulta a su nombre. '
//         );
//         if (!ok) return;

//         this.generando = true;
//         const dto: GenerarOrdenRequestDto = {
//             tipo: this.tipoOrdenId, // ← ahora dinámico
//             usuario: this.userId!,
//         };

//         this.consultasSrv.generarConMensaje(dto).subscribe({
//             next: (r) => {
//                 this.generando = false;
//                 this.generada = true;
//                 this.mostrandoPreview = true;

//                 this.fecha = new Date(r.data!.fecha);
//                 this.modeloOrden.nroOrden =
//                     r.data!.numeroOrden || this.modeloOrden.nroOrden;
//                 this.modeloOrden.apellidoNombre =
//                     r.data!.nombreApellido || this.modeloOrden.apellidoNombre;
//                 this.modeloOrden.identificacion =
//                     r.data!.numeroBeneficiario ||
//                     this.modeloOrden.identificacion;
//                 this.modeloOrden.plan = r.data!.plan || this.modeloOrden.plan;
//                 this.modeloOrden.sede = r.data!.sede || this.modeloOrden.sede;
//                 this.modeloOrden.edad = String(
//                     r.data!.edad ?? this.modeloOrden.edad
//                 );
//             },
//             error: (e: Error) => {
//                 this.generando = false;
//                 this.mensajeValidacion =
//                     e.message || 'No se pudo generar la orden.';
//                 this.puedeGenerar = false;
//             },
//         });
//     }

//     /* ====== (lo demás de tu componente: compartirWhatsApp, PDF, etc.) ====== */
//     // ... deja todo lo que ya tenías de PDF/WhatsApp sin cambios ...

//     private generarNumeroOrdenFake(): string {
//         const rnd = Math.floor(100000 + Math.random() * 900000);
//         return `ORD-${rnd}`;
//     }

//     private pedirConfirmacion(
//         titulo: string,
//         mensaje: string
//     ): Promise<boolean> {
//         return new Promise<boolean>((resolve) => {
//             const ref = this.dialog.open(SimpleConfirmDialog, {
//                 width: '380px',
//                 data: { titulo, mensaje },
//             });
//             ref.afterClosed().subscribe((res) => resolve(!!res));
//         });
//     }

//     togglePreview(): void {
//         this.mostrandoPreview = !this.mostrandoPreview;
//     }

//     private ensureHtml2Pdf(): Promise<void> {
//         return new Promise((resolve, reject) => {
//             if ((window as any).html2pdf) return resolve();
//             const s = document.createElement('script');
//             s.src =
//                 'https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js';
//             s.onload = () => resolve();
//             s.onerror = () =>
//                 reject(new Error('No se pudo cargar html2pdf.js'));
//             document.body.appendChild(s);
//         });
//     }

//     // ================== HELPERS DE DEBUG ==================
//     private debug = true;

//     private lg(step: string, data?: any) {
//         if (!this.debug) return;
//         // Usá un tag para filtrar fácil en DevTools
//         if (data !== undefined) {
//             console.log(`[ORDEN-PDF] ${step}`, data);
//         } else {
//             console.log(`[ORDEN-PDF] ${step}`);
//         }
//     }

//     private waitNextFrame(): Promise<void> {
//         return new Promise<void>((resolve) => {
//             requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
//         });
//     }

//     private async waitImagesLoaded(root: HTMLElement): Promise<void> {
//         const imgs = Array.from(
//             root.querySelectorAll('img')
//         ) as HTMLImageElement[];
//         this.lg('imgs.count', imgs.length);
//         await Promise.all(
//             imgs.map((img, i) => {
//                 const info = () => ({
//                     i,
//                     src: img.currentSrc || img.src,
//                     complete: img.complete,
//                     natural: `${img.naturalWidth}x${img.naturalHeight}`,
//                 });
//                 if (img.complete) {
//                     this.lg('img.complete', info());
//                     return Promise.resolve();
//                 }
//                 return new Promise<void>((resolve) => {
//                     img.onload = () => {
//                         this.lg('img.onload', info());
//                         resolve();
//                     };
//                     img.onerror = () => {
//                         this.lg('img.onerror', info());
//                         resolve();
//                     };
//                 });
//             })
//         );
//     }

//     private dumpNodeMetrics(node: HTMLElement, tag: string) {
//         const cs = getComputedStyle(node);
//         const rect = node.getBoundingClientRect();
//         this.lg(`${tag}.rect`, {
//             w: rect.width,
//             h: rect.height,
//             x: rect.x,
//             y: rect.y,
//         });
//         this.lg(`${tag}.styles`, {
//             display: cs.display,
//             visibility: cs.visibility,
//             opacity: cs.opacity,
//             position: cs.position,
//             left: cs.left,
//             top: cs.top,
//             zIndex: cs.zIndex,
//             overflow: `${cs.overflow} / ${cs.overflowX} / ${cs.overflowY}`,
//             transform: cs.transform,
//             filter: cs.filter,
//             clipPath: cs.clipPath,
//         });
//         this.lg(`${tag}.offset`, {
//             ow: node.offsetWidth,
//             oh: node.offsetHeight,
//             cw: node.clientWidth,
//             ch: node.clientHeight,
//         });
//         this.lg(`${tag}.html.len`, node.outerHTML.length);
//     }
//     // =======================================================

//     // ========== helpers (dejá los que ya tenés) ==========
//     // this.lg, waitNextFrame, waitImagesLoaded, dumpNodeMetrics
//     // =====================================================
//     /** Carga imagen y la convierte a dataURL PNG (seguro para pdfMake). */
//     private loadImageAsDataURL(url: string): Promise<string> {
//         return new Promise((resolve, reject) => {
//             const img = new Image();
//             img.crossOrigin = 'anonymous'; // por si algún día está en CDN
//             img.onload = () => {
//                 try {
//                     const cvs = document.createElement('canvas');
//                     cvs.width = img.naturalWidth || img.width;
//                     cvs.height = img.naturalHeight || img.height;
//                     const ctx = cvs.getContext('2d');
//                     if (!ctx)
//                         return reject(
//                             new Error('No se pudo crear el contexto 2D')
//                         );
//                     ctx.drawImage(img, 0, 0);
//                     const dataURL = cvs.toDataURL('image/png'); // SIEMPRE PNG válido
//                     // Log útil
//                     console.log('[PDF] bg loaded:', {
//                         w: cvs.width,
//                         h: cvs.height,
//                         len: dataURL.length,
//                     });
//                     resolve(dataURL);
//                 } catch (e) {
//                     reject(e);
//                 }
//             };
//             img.onerror = () =>
//                 reject(new Error('No se pudo cargar la imagen: ' + url));
//             // cache-buster para evitar versiones viejas del dev-server
//             img.src = url + (url.includes('?') ? '&' : '?') + 'v=' + Date.now();
//         });
//     }

//     async descargarPDF(): Promise<void> {
//         // 1) Asegurar pdfMake y cargar la plantilla (fondo)
//         await this.ensurePdfMake();
//         const bgDataUrl = await this.loadImageAsDataURL(
//             'assets/pdf/orden-consulta-plantilla.png'
//         );

//         // 2) Constantes para A4 en puntos (pdfMake trabaja en pt)
//         const A4_W_PT = 595.28;
//         const A4_H_PT = 841.89;

//         // 3) (Opcional) Cuadrícula de calibración
//         //    Si no tenés flags en la clase, podés setearlos acá:
//         // ======= Debug/calibración de PDF =======
//         // const SHOW_GRID = (this as any).SHOW_GRID ?? true; // poné false cuando termines de calibrar
//         const SHOW_GRID = false;
//         const GRID_STEP_MM = (this as any).GRID_STEP_MM ?? 5; // cada 5 mm
//         const GRID_BOLD_EVERY = (this as any).GRID_BOLD_EVERY ?? 10;

//         const buildGridInline = () => {
//             if (!SHOW_GRID) return { background: [], labels: [] as any[] };

//             const mm = this.mm;
//             const W = A4_W_PT,
//                 H = A4_H_PT;
//             const maxXmm = 210,
//                 maxYmm = 297;
//             const thin = { color: '#d0d0d0', lineWidth: 0.3 };
//             const bold = { color: '#b0b0b0', lineWidth: 0.7 };

//             const canvas: any[] = [];
//             // Verticales
//             for (let xmm = 0; xmm <= maxXmm; xmm += GRID_STEP_MM) {
//                 const x = mm(xmm);
//                 const style = xmm % GRID_BOLD_EVERY === 0 ? bold : thin;
//                 canvas.push({
//                     type: 'line',
//                     x1: x,
//                     y1: 0,
//                     x2: x,
//                     y2: H,
//                     ...style,
//                 });
//             }
//             // Horizontales
//             for (let ymm = 0; ymm <= maxYmm; ymm += GRID_STEP_MM) {
//                 const y = mm(ymm);
//                 const style = ymm % GRID_BOLD_EVERY === 0 ? bold : thin;
//                 canvas.push({
//                     type: 'line',
//                     x1: 0,
//                     y1: y,
//                     x2: W,
//                     y2: y,
//                     ...style,
//                 });
//             }

//             // Etiquetas de ejes cada 10mm (arriba e izquierda)
//             const labels: any[] = [];
//             const labelStyle = { fontSize: 6, color: '#9aa0a6' };
//             for (let xmm = 0; xmm <= maxXmm; xmm += GRID_BOLD_EVERY) {
//                 const x = mm(xmm);
//                 labels.push({
//                     text: String(xmm),
//                     absolutePosition: { x: Math.max(0, x - mm(1)), y: mm(1) },
//                     ...labelStyle,
//                 });
//             }
//             for (let ymm = 0; ymm <= maxYmm; ymm += GRID_BOLD_EVERY) {
//                 const y = mm(ymm);
//                 labels.push({
//                     text: String(ymm),
//                     absolutePosition: { x: mm(1), y: Math.max(0, y - mm(1)) },
//                     ...labelStyle,
//                 });
//             }

//             return { background: [{ canvas }], labels };
//         };

//         const grid = buildGridInline();

//         // 4) Tus overlays (SOLO valores; los rótulos vienen en la plantilla)
//         const overlays: any[] = [
//             // Cabecera derecha
//             {
//                 text: String(this.modeloOrden.nroOrden),
//                 bold: true,
//                 fontSize: this.FONT.big,
//                 absolutePosition: {
//                     x: this.mm(this.POS.nroOrden.x),
//                     y: this.mm(this.POS.nroOrden.y),
//                 },
//             },
//             {
//                 text: this.fecha.toLocaleDateString('es-AR'),
//                 fontSize: this.FONT.normal,
//                 absolutePosition: {
//                     x: this.mm(this.POS.fecha.x),
//                     y: this.mm(this.POS.fecha.y),
//                 },
//             },

//             // Datos
//             {
//                 text: String(this.modeloOrden.identificacion || ''),
//                 fontSize: this.FONT.normal,
//                 absolutePosition: {
//                     x: this.mm(this.POS.benef.x),
//                     y: this.mm(this.POS.benef.y),
//                 },
//             },
//             {
//                 text: String(this.modeloOrden.plan || ''),
//                 fontSize: this.FONT.normal,
//                 absolutePosition: {
//                     x: this.mm(this.POS.plan.x),
//                     y: this.mm(this.POS.plan.y),
//                 },
//             },
//             {
//                 text: String(this.modeloOrden.apellidoNombre || ''),
//                 fontSize: this.FONT.normal,
//                 absolutePosition: {
//                     x: this.mm(this.POS.apellido.x),
//                     y: this.mm(this.POS.apellido.y),
//                 },
//             },
//             {
//                 text: String(this.modeloOrden.edad || ''),
//                 fontSize: this.FONT.normal,
//                 absolutePosition: {
//                     x: this.mm(this.POS.edad.x),
//                     y: this.mm(this.POS.edad.y),
//                 },
//             },
//             {
//                 text: String(this.modeloOrden.prestador || 'HOSPITAL ESPAÑOL'),
//                 bold: true,
//                 fontSize: this.FONT.normal,
//                 absolutePosition: {
//                     x: this.mm(this.POS.prestador.x),
//                     y: this.mm(this.POS.prestador.y),
//                 },
//             },
//         ];

//         // 5) DocDefinition: fondo + cuadrícula + etiquetas + overlays
//         const docDefinition: any = {
//             pageSize: 'A4',
//             pageMargins: [0, 0, 0, 0],
//             images: { bg: bgDataUrl },
//             background: [
//                 { image: 'bg', width: A4_W_PT, height: A4_H_PT }, // plantilla
//                 ...grid.background, // cuadrícula encima
//             ],
//             content: [
//                 ...grid.labels, // números de ejes
//                 ...overlays, // tus datos
//             ],
//             defaultStyle: { fontSize: this.FONT.normal },
//         };

//         // 6) Descargar
//         (window as any).pdfMake
//             .createPdf(docDefinition)
//             .download(`${this.modeloOrden.nroOrden}.pdf`);
//     }

//     /** Carga pdfMake + fuentes (vfs) desde CDN una sola vez */
//     private ensurePdfMake(): Promise<void> {
//         return new Promise((resolve, reject) => {
//             const w = window as any;
//             if (w.pdfMake && w.pdfMake.vfs) return resolve();

//             const add = (src: string) =>
//                 new Promise<void>((res, rej) => {
//                     const s = document.createElement('script');
//                     s.src = src;
//                     s.onload = () => res();
//                     s.onerror = () =>
//                         rej(new Error(`No se pudo cargar ${src}`));
//                     document.body.appendChild(s);
//                 });

//             add(
//                 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.2.9/pdfmake.min.js'
//             )
//                 .then(() =>
//                     add(
//                         'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.2.9/vfs_fonts.min.js'
//                     )
//                 )
//                 .then(() => resolve())
//                 .catch(reject);
//         });
//     }

//     /** Convierte una imagen local (assets) a dataURL base64 para pdfMake */
//     private async imgToDataURL(src: string): Promise<string> {
//         const res = await fetch(src, { cache: 'no-cache' });
//         const blob = await res.blob();
//         return new Promise<string>((resolve, reject) => {
//             const reader = new FileReader();
//             reader.onload = () => resolve(reader.result as string);
//             reader.onerror = reject;
//             reader.readAsDataURL(blob);
//         });
//     }

//     private overTxt(name: PosKey, text: string, opts?: any): any[] {
//         const p = this.POS[name];
//         const d = this.DELTA[name] || { dx: 0, dy: 0 };
//         const x = this.mm(p.x + (d.dx || 0));
//         const y = this.mm(p.y + (d.dy || 0));

//         const node: any = {
//             text,
//             absolutePosition: { x, y },
//             fontSize: this.FONT.normal,
//             ...(opts || {}),
//         };

//         if (this.DEBUG_GUIDES) {
//             return [
//                 {
//                     canvas: [
//                         {
//                             type: 'line',
//                             x1: x - 6,
//                             y1: y,
//                             x2: x + 6,
//                             y2: y,
//                             lineWidth: 0.5,
//                             color: '#ff0066',
//                         },
//                         {
//                             type: 'line',
//                             x1: x,
//                             y1: y - 6,
//                             x2: x,
//                             y2: y + 6,
//                             lineWidth: 0.5,
//                             color: '#ff0066',
//                         },
//                     ],
//                 },
//                 {
//                     canvas: [
//                         {
//                             type: 'rect',
//                             x: x - 2,
//                             y: y - 10,
//                             w: this.mm(60),
//                             h: this.mm(7),
//                             color: '#ff006622',
//                         },
//                     ],
//                 },
//                 node,
//             ];
//         }
//         return [node];
//     }

//     // === GENERADOR DE CUADRÍCULA (líneas + etiquetas) ===
//     private buildGridBackground() {
//         const mm = this.mm;
//         const W = this.A4_W_PT,
//             H = this.A4_H_PT;
//         const step = this.GRID_STEP_MM; // 5 mm
//         const boldEvery = this.GRID_BOLD_EVERY; // 10 mm
//         const maxXmm = 210,
//             maxYmm = 297;

//         const thin = { color: '#d0d0d0', lineWidth: 0.3 };
//         const bold = { color: '#b0b0b0', lineWidth: 0.7 };

//         const canvas: any[] = [];

//         // Líneas verticales
//         for (let xmm = 0; xmm <= maxXmm; xmm += step) {
//             const x = mm(xmm);
//             const style = xmm % boldEvery === 0 ? bold : thin;
//             canvas.push({ type: 'line', x1: x, y1: 0, x2: x, y2: H, ...style });
//         }

//         // Líneas horizontales
//         for (let ymm = 0; ymm <= maxYmm; ymm += step) {
//             const y = mm(ymm);
//             const style = ymm % boldEvery === 0 ? bold : thin;
//             canvas.push({ type: 'line', x1: 0, y1: y, x2: W, y2: y, ...style });
//         }

//         // Etiquetas cada 10mm en ejes (arriba y izquierda)
//         const labels: any[] = [];
//         const labelStyle = { fontSize: 6, color: '#9aa0a6' }; // gris tenue

//         // Eje X (arriba)
//         for (let xmm = 0; xmm <= maxXmm; xmm += boldEvery) {
//             const x = mm(xmm);
//             labels.push({
//                 text: String(xmm),
//                 absolutePosition: { x: Math.max(0, x - mm(1)), y: mm(1) }, // 1mm desde el borde
//                 ...labelStyle,
//             });
//         }

//         // Eje Y (izquierda)
//         for (let ymm = 0; ymm <= maxYmm; ymm += boldEvery) {
//             const y = mm(ymm);
//             labels.push({
//                 text: String(ymm),
//                 absolutePosition: { x: mm(1), y: Math.max(0, y - mm(1)) },
//                 ...labelStyle,
//             });
//         }

//         // Devolvemos nodos: el canvas va en background, etiquetas en content (antes de overlays)
//         return {
//             background: [{ canvas }], // ← se pintará sobre el fondo-imagen
//             labels, // ← se agregan al content antes de tus overlays
//         };
//     }

//     // Construye docDefinition (mismo contenido que usás en descargarPDF)
//     private async buildDocDefinition(): Promise<any> {
//         await this.ensurePdfMake();
//         const bgDataUrl = await this.loadImageAsDataURL(
//             'assets/pdf/orden-consulta-plantilla.png'
//         );

//         const A4_W_PT = 595.28,
//             A4_H_PT = 841.89;

//         const overlays: any[] = [
//             {
//                 text: String(this.modeloOrden.nroOrden),
//                 bold: true,
//                 fontSize: this.FONT.big,
//                 absolutePosition: {
//                     x: this.mm(this.POS.nroOrden.x),
//                     y: this.mm(this.POS.nroOrden.y),
//                 },
//             },
//             {
//                 text: this.fecha.toLocaleDateString('es-AR'),
//                 fontSize: this.FONT.normal,
//                 absolutePosition: {
//                     x: this.mm(this.POS.fecha.x),
//                     y: this.mm(this.POS.fecha.y),
//                 },
//             },
//             {
//                 text: String(this.modeloOrden.identificacion || ''),
//                 fontSize: this.FONT.normal,
//                 absolutePosition: {
//                     x: this.mm(this.POS.benef.x),
//                     y: this.mm(this.POS.benef.y),
//                 },
//             },
//             {
//                 text: String(this.modeloOrden.plan || ''),
//                 fontSize: this.FONT.normal,
//                 absolutePosition: {
//                     x: this.mm(this.POS.plan.x),
//                     y: this.mm(this.POS.plan.y),
//                 },
//             },
//             {
//                 text: String(this.modeloOrden.apellidoNombre || ''),
//                 fontSize: this.FONT.normal,
//                 absolutePosition: {
//                     x: this.mm(this.POS.apellido.x),
//                     y: this.mm(this.POS.apellido.y),
//                 },
//             },
//             {
//                 text: String(this.modeloOrden.edad || ''),
//                 fontSize: this.FONT.normal,
//                 absolutePosition: {
//                     x: this.mm(this.POS.edad.x),
//                     y: this.mm(this.POS.edad.y),
//                 },
//             },
//             {
//                 text: String(this.modeloOrden.prestador || 'HOSPITAL ESPAÑOL'),
//                 bold: true,
//                 fontSize: this.FONT.normal,
//                 absolutePosition: {
//                     x: this.mm(this.POS.prestador.x),
//                     y: this.mm(this.POS.prestador.y),
//                 },
//             },
//         ];

//         return {
//             pageSize: 'A4',
//             pageMargins: [0, 0, 0, 0],
//             images: { bg: bgDataUrl },
//             background: [{ image: 'bg', width: A4_W_PT, height: A4_H_PT }],
//             content: overlays,
//             defaultStyle: { fontSize: this.FONT.normal },
//         };
//     }

//     // Genera un Blob del PDF (para compartir/adjuntar)
//     private async getPdfBlob(): Promise<{
//         blob: Blob;
//         filename: string;
//         url: string;
//     }> {
//         const docDefinition = await this.buildDocDefinition();
//         const filename = `${this.modeloOrden.nroOrden}.pdf`;

//         return new Promise((resolve) => {
//             (window as any).pdfMake
//                 .createPdf(docDefinition)
//                 .getBlob((blob: Blob) => {
//                     const url = URL.createObjectURL(blob);
//                     resolve({ blob, filename, url });
//                 });
//         });
//     }

//     private isAndroid(): boolean {
//         return /Android/i.test(navigator.userAgent);
//     }
//     private isIOS(): boolean {
//         return /iPhone|iPad|iPod/i.test(navigator.userAgent);
//     }

//     /** Descarga un Blob como archivo (carpeta Descargas del dispositivo) */
//     private downloadBlob(blob: Blob, filename: string) {
//         const url = URL.createObjectURL(blob);
//         const a = document.createElement('a');
//         a.href = url;
//         a.download = filename;
//         document.body.appendChild(a);
//         a.click();
//         a.remove();
//         setTimeout(() => URL.revokeObjectURL(url), 60_000);
//     }

//     /** Fallback web: abre WhatsApp con texto (sin adjunto) */
//     private openWhatsAppWithText(texto: string) {
//         // iOS: deep link directo
//         if (this.isIOS()) {
//             window.location.href = `whatsapp://send?text=${encodeURIComponent(
//                 texto
//             )}`;
//             return;
//         }

//         // ANDROID: intenta intent:// (abre app) y si no, wa.me (WhatsApp Web)
//         if (this.isAndroid()) {
//             const intent = `intent://send/?text=${encodeURIComponent(
//                 texto
//             )}#Intent;scheme=whatsapp;package=com.whatsapp;end`;
//             // Abre intent y, como fallback, wa.me (si el intent no funciona en el navegador actual)
//             window.location.href = intent;
//             setTimeout(() => {
//                 window.open(
//                     `https://wa.me/?text=${encodeURIComponent(texto)}`,
//                     '_blank'
//                 );
//             }, 800);
//             return;
//         }

//         // Desktop / otros
//         window.open(
//             `https://wa.me/?text=${encodeURIComponent(texto)}`,
//             '_blank'
//         );
//     }

//     async compartirWhatsApp(): Promise<void> {
//         try {
//             // 1) Generar el PDF en memoria
//             const { blob, filename } = await this.getPdfBlob();

//             // 2) Descargar el PDF (queda guardado en Descargas)
//             this.downloadBlob(blob, filename);

//             // 3) Abrir WhatsApp con un mensaje que indique cómo adjuntarlo
//             const mensaje = [
//                 'Te comparto mi orden de consulta en PDF.',
//                 `El archivo se descargó como: ${filename}`,
//                 'Abrí el clip en WhatsApp y adjuntalo desde la carpeta Descargas.',
//             ].join('\n');

//             this.openWhatsAppWithText(mensaje);
//         } catch (e) {
//             console.error('No se pudo preparar el PDF para compartir:', e);
//             // Último recurso: descarga tradicional
//             await this.descargarPDF();
//         }
//     }
//     // Compartir por WhatsApp (Web Share API si se puede; fallback a wa.me con link)
//     // async compartirWhatsApp(): Promise<void> {
//     //     try {
//     //         const { blob, filename, url } = await this.getPdfBlob();

//     //         // 1) Intento Web Share API con archivos (móvil/desktop compatible)
//     //         const file = new File([blob], filename, {
//     //             type: 'application/pdf',
//     //         });
//     //         const nav: any = navigator;

//     //         if (nav.canShare && nav.canShare({ files: [file] })) {
//     //             await nav.share({
//     //                 files: [file],
//     //                 title: 'Orden de Consulta',
//     //                 text: 'Te comparto mi orden de consulta en PDF.',
//     //             });
//     //             URL.revokeObjectURL(url);
//     //             return;
//     //         }

//     //         // 2) Fallback: abrir WhatsApp Web con link al PDF (objURL)
//     //         const texto = `Te comparto mi orden de consulta:\n${url}`;
//     //         const wa = `https://wa.me/?text=${encodeURIComponent(texto)}`;
//     //         window.open(wa, '_blank');

//     //         // Opcional: revocar el objectURL luego de unos minutos
//     //         setTimeout(() => URL.revokeObjectURL(url), 60_000);
//     //     } catch (e) {
//     //         console.error('No se pudo compartir por WhatsApp:', e);
//     //         // fallback mínimo: descargá el PDF si falló share
//     //         await this.descargarPDF();
//     //     }
//     // }
// }

// /** Diálogo simple de confirmación */
// @Component({
//     selector: 'simple-confirm-dialog',
//     template: `
//         <div class="confirm-wrap">
//             <h3>{{ data.titulo }}</h3>
//             <p>{{ data.mensaje }}</p>

//             <div class="actions">
//                 <button
//                     type="button"
//                     class="btn btn-secondary"
//                     (click)="onCancel($event)"
//                 >
//                     Cancelar
//                 </button>

//                 <!-- MISMO ESTILO QUE “GENERAR” -->
//                 <button
//                     type="button"
//                     class="btn btn-primary"
//                     (click)="onConfirm($event)"
//                     cdkFocusInitial
//                 >
//                     GENERAR
//                 </button>
//             </div>
//         </div>
//     `,
//     styles: [
//         `
//             .confirm-wrap h3 {
//                 margin: 0 0 8px;
//                 font-weight: 800;
//                 color: #333;
//             }
//             .confirm-wrap p {
//                 margin: 0 0 12px;
//                 color: #555;
//             }

//             .actions {
//                 display: flex;
//                 justify-content: center; /* centrados */
//                 align-items: center;
//                 gap: 12px; /* separación */
//                 margin-top: 12px;
//             }

//             /* ===== Botones: mismos estilos que el principal ===== */
//             .btn {
//                 min-width: 130px;
//                 padding: 10px 18px;
//                 border-radius: 8px;
//                 font-weight: 700;
//                 cursor: pointer;
//                 transition: 0.2s;
//                 display: inline-flex;
//                 align-items: center;
//                 justify-content: center;
//             }
//             .btn-primary {
//                 background: #580f2e;
//                 color: #fff;
//                 border: 2px solid #580f2e;
//             }
//             .btn-primary:hover {
//                 background: #fff;
//                 color: #580f2e;
//             }
//             .btn-secondary {
//                 background: #fff;
//                 color: #580f2e;
//                 border: 2px solid #580f2e;
//             }
//             .btn-secondary:hover {
//                 background: #580f2e;
//                 color: #fff;
//             }
//         `,
//     ],
// })
// export class SimpleConfirmDialog {
//     constructor(
//         private dialogRef: MatDialogRef<SimpleConfirmDialog>,
//         @Inject(MAT_DIALOG_DATA)
//         public data: { titulo: string; mensaje: string }
//     ) {}

//     onCancel(e: Event) {
//         e.stopPropagation();
//         this.dialogRef.close(false);
//     }
//     onConfirm(e: Event) {
//         e.stopPropagation();
//         this.dialogRef.close(true);
//     }
// }
